import React from "react";
/**
 * components/hazard/workGroup.js — Аюулын жагсаалтыг АЖЛААР бүлэглэх.
 *
 * Нэг ӨЭА/наряд ажил дээр олон аюул бүртгэгддэг. Жагсаалтад ажлын дугаар,
 * нэрийг мөр бүрт давтаж бичих нь уншихад хүндрэлтэй тул ижил ажлын мөрүүдийг
 * зэрэгцүүлж, ажлын нүдийг `rowSpan`-аар нэгтгэнэ (merge).
 *
 * Талбарын эх сурвалж — Resources/mobile/hazard_report.py `_attach_prework`:
 *   work_no   — "Наряд №2/2026" | "ӨЭА №1035"
 *   work_name — naryad_jobs.jobdesc → бүртгэлийн work_description/destination
 */

/** Ажлын ялгах түлхүүр. Ажилтай холбоогүй бол хоосон мөр буцаана. */
export const workKeyOf = (it) => {
  if (!it) return "";
  if (it.work_no) return String(it.work_no);
  if (it.naryad_job_id) return `N${it.naryad_job_id}`;
  if (it.oea_permit_id) return `O${it.oea_permit_id}`;
  return "";
};

/**
 * Мөрүүдийг ажлаар бүлэглэж, merge хийх мэдээллийг буцаана.
 *
 * Бүлгүүдийн ХАРЬЦАНГУЙ ДАРААЛАЛ хадгалагдана — өөрөөр хэлбэл хэрэглэгчийн
 * сонгосон эрэмбэ (огноо, түвшин г.м) бүлгийн түвшинд хэвээр үлдэнэ,
 * зөвхөн нэг ажлын мөрүүд зэрэгцэж ирнэ.
 *
 * Ажилгүй (work_no байхгүй) мөр бүр ТУСДАА бүлэг — санамсаргүй нийлэхгүй.
 *
 * @returns {{rows: Array, spans: number[]}}
 *   spans[i] > 0 → энэ мөрөнд ажлын нүдийг `rowSpan={spans[i]}`-аар гаргана
 *   spans[i] === 0 → өмнөх мөрийн нүд үргэлжилж байгаа тул нүд гаргахгүй
 */
export function groupRowsByWork(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const order = [];
  const groups = new Map();
  list.forEach((r, i) => {
    const key = workKeyOf(r) || `__solo_${r?.id ?? i}`;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key).push(r);
  });
  const out = [];
  const spans = [];
  order.forEach((key) => {
    const g = groups.get(key);
    g.forEach((r, i) => {
      out.push(r);
      spans.push(i === 0 ? g.length : 0);
    });
  });
  return { rows: out, spans };
}

// ── Хүснэгтийн нүднүүд ──────────────────────────────────────────────────
// /me (myHazards) ба /hazard/dashboard хоёулаа ИЖИЛ харагдана.

/** Ажлын төрлийн өнгө — ӨЭА улбар шар · Наряд цэнхэр. */
export const workColorOf = (it) =>
  it?.prework_item_type === "CRITICAL"
    ? { bg: "#FFF3E6", border: "#FFD3A8", fg: "#C2410C" }
    : { bg: "#EAF2FE", border: "#C3D8F7", fg: "#1B4C8F" };

// Нэгтгэсэн (rowSpan) нүдний ерөнхий загвар — олон мөрийг хамарна
const mergedCellStyle = {
  padding: "10px 12px",
  verticalAlign: "top",
  background: "#FBFCFD",
  borderRight: "1px solid #EDEFF3",
  whiteSpace: "nowrap",
};

/** АЖЛЫН ДУГААР баганын нүд — нэг ажлын бүх мөрийг хамарна. */
export function WorkNoCell({ it, span }) {
  const c = workColorOf(it);
  return (
    <td rowSpan={span} style={{ ...mergedCellStyle, width: 130 }}>
      {it.work_no ? (
        <span
          style={{
            display: "inline-block",
            padding: "2px 9px",
            borderRadius: 999,
            background: c.bg,
            border: `1px solid ${c.border}`,
            color: c.fg,
            fontSize: 11,
            fontWeight: 800,
          }}
        >
          <i className="fas fa-briefcase" style={{ marginRight: 5 }} />
          {it.work_no}
        </span>
      ) : (
        <span style={{ color: "#C4CAD4", fontSize: 12 }}>—</span>
      )}
      {/* Хэдэн аюул нэгтгэгдсэнийг харуулна */}
      {span > 1 ? (
        <div style={{ fontSize: 10.5, color: "#8A94A6", marginTop: 4 }}>
          {span} аюул
        </div>
      ) : null}
    </td>
  );
}

/** АЖЛЫН НЭР баганын нүд — нэг ажлын бүх мөрийг хамарна. */
export function WorkNameCell({ it, span }) {
  return (
    <td
      rowSpan={span}
      style={{ ...mergedCellStyle, whiteSpace: "normal", maxWidth: 220 }}
      title={it.work_name || ""}
    >
      {it.work_name ? (
        <div
          style={{
            fontSize: 12,
            color: "#3A4150",
            fontWeight: 600,
            lineHeight: 1.4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {it.work_name}
        </div>
      ) : (
        <span style={{ color: "#C4CAD4", fontSize: 12 }}>—</span>
      )}
    </td>
  );
}
